import { getItem } from 'app/components/analytics'

export const trackCreateReturnExchange = (externalOrderId) => {
    return {
        event: 'open_returns_and_exchanges',
        eventEffect: 'start exchange or return process',
        category: { primaryCategory: 'my account', subCategory: 'my orders' },
        attributes: {
            orderNumber: externalOrderId,
        },
        lpTag: {
            additionalSections: ['returns'],
        },
    }
}

export const trackCreateReturnExchangeItem = (item, orderNumber, locale) => {
    const attributes = {
        exchangeType: item.returnOrExchange,
        returnReason: item.selectedReturnCode,
    }

    const { currency: orderCurrency, country: countryCode } = locale

    return {
        event: 'refund-exchange - select item',
        effect: 'view item for exchange or return information',
        category: {
            primaryCategory: 'ecommerce',
            subCategory: 'my orders',
        },
        attributes: {
            orderNumber,
            orderCurrency,
            countryCode,
        },
        return: {
            item: [getItem({ item, attributes })],
        },
    }
}

export const trackCreateReturnContinueExchange = (returnItems, orderNumber, locale) => {
    const items = returnItems?.map((item) => {
        const attributes = {
            exchangeType: item.returnOrExchange,
            returnReason: item.selectedReturnCode,
        }

        return getItem({ item, attributes })
    })

    const { currency: orderCurrency, country: countryCode } = locale

    return {
        event: 'refund-exchange - continue order',
        effect: 'start refund-exchange',
        category: {
            primaryCategory: 'ecommerce',
            subCategory: 'my orders',
        },
        attributes: {
            orderNumber,
            orderCurrency,
            countryCode,
        },
        return: {
            item: items,
        },
    }
}

export const trackCreateReturnCancelExchange = (orderNumber, locale) => {
    const { currency: orderCurrency, country: countryCode } = locale

    return {
        event: 'refund-exchange - cancel order',
        effect: 'cancelled exchange or return process',
        category: {
            primaryCategory: 'my account',
            subCategory: 'my orders',
        },
        attributes: {
            orderNumber,
            orderCurrency,
            countryCode,
        },
    }
}

export const trackSelectReturnMethod = (externalOrderId, locale) => {
    const { currency: orderCurrency, country: countryCode } = locale

    return {
        event: 'refund-exchange - book return - select return method',
        eventEffect: 'user select return method',
        category: {
            pageType: 'account management',
            primaryCategory: 'ecommerce',
            subCategory: 'book a return',
        },
        attributes: {
            orderNumber: externalOrderId,
            orderCurrency,
            countryCode,
        },
    }
}

export const trackReturnConfirmation = (externalOrderId, locale) => {
    const { currency: orderCurrency, country: countryCode } = locale

    return {
        event: 'refund-exchange - book return - confirmation page',
        effect: 'refund-exchange confirmation page viewed',
        category: {
            pageType: 'account management',
            primaryCategory: 'ecommerce',
            subCategory: 'my orders',
        },
        attributes: {
            orderId: externalOrderId,
            orderCurrency,
            countryCode,
        },
    }
}

export const trackReturnCollectionSkipped = (externalOrderId, locale) => {
    const { currency: orderCurrency, country: countryCode } = locale

    return {
        event: 'refund-exchange - book return - skipped',
        category: {
            pageType: 'account management',
            primaryCategory: 'ecommerce',
            subCategory: 'book a return',
        },
        attributes: {
            orderNumber: externalOrderId,
            orderCurrency,
            countryCode,
        },
    }
}

export const trackDropOffComplete = (returnItems, orderNumber, locale, courier) => {
    let attributes

    const items = returnItems?.map((item) => {
        attributes = {
            exchangeType: item.returnOrExchange,
            returnReason: item.selectedReturnCode,
        }

        return getItem({ item, attributes })
    })

    const { country: countryCode } = locale

    return {
        event: 'refund-exchange - book return - complete drop off',
        category: {
            pageType: 'account management',
            primaryCategory: 'ecommerce',
            subCategory: 'book a return - drop off selected',
        },
        return: {
            // Items being returned
            item: items,
        },
        attributes: {
            exchangeType: attributes.exchangeType,
            returnReason: attributes.selectedReturnCode,
            courier: courier,
            method: 'drop off',
            cost: '',
            pickupCountryCode: countryCode,
            orderId: orderNumber,
        },
    }
}

export const trackDropOffLocator = (courier) => {
    return {
        event: 'refund-exchange - find courier drop off location',
        category: {
            pageType: 'account management',
            primaryCategory: 'ecommerce',
            subCategory: 'book a return',
        },
        attributes: {
            courier: courier,
        },
    }
}

export const trackReturnCollectionComplete = (returnItems, orderNumber, locale, courier) => {
    let attributes

    const items = returnItems?.map((item) => {
        attributes = {
            exchangeType: item.returnOrExchange,
            returnReason: item.selectedReturnCode,
        }

        return getItem({ item, attributes })
    })

    const { country: countryCode } = locale

    return {
        event: 'refund-exchange - book return - complete collection',
        category: {
            pageType: 'account management',
            primaryCategory: 'ecommerce',
            subCategory: 'book a return - collection selected',
        },
        return: {
            // Items being returned
            item: items,
        },
        attributes: {
            exchangeType: attributes.exchangeType,
            returnReason: attributes.selectedReturnCode,
            courier: courier,
            method: 'collection',
            cost: '',
            pickupCountryCode: countryCode,
            orderId: orderNumber,
        },
    }
}

export const trackOrderOrReturnCTA = (eventType, CTAType) => {
    const eventEffect = {
        order: 'track my order',
        return: 'track returned item',
    }

    return {
        event: `track your ${eventType} - ${CTAType}`,
        effect: eventEffect[eventType],
        category: {
            primaryCategory: 'my account',
            subCategory: 'my orders',
        },
    }
}

export const trackBookCollectionCTA = () => {
    return {
        event: 'book your collection - cta',
        effect: 'return item',
        category: {
            primaryCategory: 'my account',
            subCategory: 'my orders',
        },
    }
}

const buildOrderDetailTrackingObject = ({
    externalOrderId,
    orderPrimaryStatus,
    orderTrackingStatus,
    orderDateRaw,
    orderEstimatedDeliveryDate,
    courier,
    currentLocation,
}) => ({
    id: externalOrderId,
    currentStatus: orderPrimaryStatus,
    currentSecondaryStatus: orderTrackingStatus,
    placedDate: formatDate(orderDateRaw),
    deliveryEstimateDate: formatDate(orderEstimatedDeliveryDate),
    courier,
    currentLocation,
})

export const trackOrderDetailsTracking = (orders) => {
    const formattedOrders = orders.map((order) => buildOrderDetailTrackingObject(order))
    return {
        event: 'order tracking module load',
        category: {
            primaryCategory: 'ecommerce',
            subCategory: 'order tracking',
        },
        orders: formattedOrders,
    }
}

const formatDate = (date) => {
    if (!date) return ''
    const formattedDate = new Date(date)
    formattedDate.setUTCHours(0, 0, 0, 0)
    return formattedDate.toISOString()
}
